/**
 * One status → colour vocabulary for the whole dashboard.
 *
 * vista#792 item 4 reported the Topology "Agent" node showing a **grey** dot
 * while its state was `up`, next to Uplink/Internet nodes showing green for
 * `healthy`. The cause was not a missing case — it was a second, narrower copy
 * of this map living privately in `Topology.jsx` with only
 * `healthy | degraded | down | unknown`. Anything else fell through to grey.
 *
 * So the fix is one vocabulary rather than one more entry, and consolidating it
 * turned up two more values with no mapping:
 *
 * - `up` — the agent node's hardcoded status. The reported bug.
 * - `discovered` / `revoked` — real `proxies.status` values. Grey is right for
 *   `discovered` (seen, not yet adopted — nothing is being claimed about its
 *   health) and misleading for `revoked`, which is an operator ACTION rather
 *   than a fault. It takes slate, following the `unmonitored` precedent: the
 *   system is deliberately not watching it, and red would read as an outage.
 *
 * Keys come from three places, which is why the list is long: `devices.status`,
 * `proxies.status`, and the health rollups (`healthy`/`degraded`/`critical`).
 */

/** Badge background + text, for a pill. */
export const STATUS_STYLES = {
  // Up and healthy.
  up: 'bg-green-100 text-green-700',
  active: 'bg-green-100 text-green-700',
  healthy: 'bg-green-100 text-green-700',
  // Degraded but reachable.
  warning: 'bg-yellow-100 text-yellow-700',
  degraded: 'bg-yellow-100 text-yellow-700',
  // Confirmed bad.
  critical: 'bg-red-100 text-red-700',
  down: 'bg-red-100 text-red-700',
  // A proxy we expect to hear from every 30s and haven't is a fault the
  // operator should SEE, not a muted unknown — one sat in the list as green
  // "active" while 41 days dead.
  offline: 'bg-red-100 text-red-700',
  // vista#616: a device no proxy is monitoring is not "down" (red, an outage)
  // nor "unknown" (grey, never-classified) — its own muted slate, so operators
  // read it as "intentionally not watched", not dead.
  unmonitored: 'bg-slate-100 text-slate-600',
  // `stale` is the opposite claim to `down`: not that the box is unwell, but
  // that we decline to assert anything because the sample is too old to mean
  // anything. Slate, alongside `unmonitored`.
  stale: 'bg-slate-100 text-slate-600',
  // vista#792: an operator deauthorized this proxy. Not a fault — slate, for
  // the same reason as `unmonitored`.
  revoked: 'bg-slate-100 text-slate-600',
  // Nothing asserted yet.
  inactive: 'bg-gray-100 text-gray-500',
  unknown: 'bg-gray-100 text-gray-500',
  disconnected: 'bg-gray-100 text-gray-500',
  discovered: 'bg-gray-100 text-gray-500',
  // In flight.
  info: 'bg-blue-100 text-blue-700',
  pending: 'bg-purple-100 text-purple-700',
};

/** The dot beside a label, and the topology node's status dot. */
export const DOT_STYLES = {
  up: 'bg-green-500',
  active: 'bg-green-500',
  healthy: 'bg-green-500',
  warning: 'bg-yellow-500',
  degraded: 'bg-yellow-500',
  critical: 'bg-red-500',
  down: 'bg-red-500',
  offline: 'bg-red-500',
  unmonitored: 'bg-slate-400',
  stale: 'bg-slate-400',
  revoked: 'bg-slate-400',
  inactive: 'bg-gray-400',
  unknown: 'bg-gray-400',
  disconnected: 'bg-gray-400',
  discovered: 'bg-gray-400',
  info: 'bg-blue-500',
  pending: 'bg-purple-500',
};

/**
 * Border + fill for a topology node card, derived from the same vocabulary so a
 * node's border, its fill and its dot cannot disagree.
 */
export const NODE_STYLES = {
  green: { border: 'border-green-500', bg: 'bg-green-50' },
  yellow: { border: 'border-yellow-500', bg: 'bg-yellow-50' },
  red: { border: 'border-red-500', bg: 'bg-red-50' },
  slate: { border: 'border-slate-400', bg: 'bg-slate-50' },
  gray: { border: 'border-gray-400', bg: 'bg-gray-50' },
  blue: { border: 'border-blue-500', bg: 'bg-blue-50' },
  purple: { border: 'border-purple-500', bg: 'bg-purple-50' },
};

/** The dot class for a status, defaulting to `unknown` rather than to nothing. */
export function statusDot(status) {
  return DOT_STYLES[status] || DOT_STYLES.unknown;
}

/** The badge class for a status. */
export function statusStyle(status) {
  return STATUS_STYLES[status] || STATUS_STYLES.unknown;
}

/**
 * The node card's border + fill for a status.
 *
 * Read off the dot class so there is exactly one place a status's colour is
 * decided — a node whose border said green and whose dot said grey is the
 * defect this replaces.
 */
export function statusNode(status) {
  const dot = statusDot(status);
  const family = dot.replace('bg-', '').replace(/-\d+$/, '');
  return NODE_STYLES[family] || NODE_STYLES.gray;
}

/**
 * Syslog severity → colour. A SEPARATE map from `STATUS_STYLES`, deliberately.
 *
 * §3.12 says a status maps to a colour in exactly one place, and this obeys
 * that — it just recognises that syslog severity is a *different* vocabulary
 * that happens to overlap on three keys. `STATUS_STYLES` is keyed by
 * `devices.status`, `proxies.status` and the health rollups; RFC 5424 adds
 * `emerg`/`alert`/`crit`/`err`/`notice`/`debug`, and vendors emit both spellings
 * of several of them.
 *
 * Merging the two would mean one map serving two vocabularies, so a change to a
 * device status colour would silently restyle log output — and `critical`
 * (a health rollup) and `crit` (a syslog level) would have to agree forever.
 * Two maps in one file keeps "one place per vocabulary" without conflating them.
 */
export const SEVERITY_STYLES = {
  // System is unusable / action required immediately.
  emerg: 'bg-red-100 text-red-800',
  emergency: 'bg-red-100 text-red-800',
  alert: 'bg-red-100 text-red-800',
  crit: 'bg-red-100 text-red-700',
  critical: 'bg-red-100 text-red-700',
  // Errors.
  err: 'bg-orange-100 text-orange-700',
  error: 'bg-orange-100 text-orange-700',
  // Warnings.
  warning: 'bg-yellow-100 text-yellow-700',
  warn: 'bg-yellow-100 text-yellow-700',
  // Normal but significant.
  notice: 'bg-blue-100 text-blue-700',
  info: 'bg-blue-100 text-blue-700',
  informational: 'bg-blue-100 text-blue-700',
  // Chatter.
  debug: 'bg-gray-100 text-gray-500',
  // A line whose sender sent no severity. Grey, because nothing is asserted —
  // the same reasoning as `discovered` above, and NOT red: a malformed line is
  // not evidence of a fault.
  unknown: 'bg-gray-100 text-gray-500',
};

/** The badge class for a syslog severity, case-insensitively. */
export function severityStyle(severity) {
  const key = String(severity ?? '').toLowerCase();
  return SEVERITY_STYLES[key] || SEVERITY_STYLES.unknown;
}

/**
 * Severities that count as "warning or worse" — the one-click filter on the
 * Logs page, and the same list the agent exposes as `PROBLEM_SEVERITIES`.
 *
 * A LIST rather than an ordinal comparison because the column is text and
 * carries both RFC and vendor spellings; ranking `err` against `error` against
 * `Error` would need the very normalisation this list makes unnecessary.
 */
export const PROBLEM_SEVERITIES = [
  'emerg',
  'alert',
  'crit',
  'critical',
  'err',
  'error',
  'warning',
  'warn',
];
